import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Account } from "@/api/rest/models/account";
import { HouseholdData } from "@/api/rest/models/household";
import { CaseType, NewWarrantyCaseResponseType } from "@/api/rest/models/warranty";
import {
  deleteWarrantyDraft,
  fileNewCase,
  forceSubmitCase,
  getLatestWarrantyDraft,
  WarrantyDraftFlow,
} from "@/api/rest/services/warrantyService";

import { Banner } from "@/components/common/Banner";
import Button from "@/components/common/Button";
import { ChatMessage, ChatMessageHolder, ChatMessageType } from "@/components/common/ChatMessage.tsx";
import { isSubmitShortcut } from "@/utils/keyboard";
import { sendToSentry } from "@/utils/sentry";

import { mergeIssues, rememberedPhone, rememberPhone, useIssueQueue } from "./issueQueue";
import { intakeVerdict } from "./intakeVerdict";
import { createRequestGate } from "./requestGate";
import { isSelfCheckDraft } from "./selfCheck";
import SelfCheckTip from "./SelfCheckTip";
import SubmissionForm from "./SubmissionForm";
import { useSelfCheck } from "./useSelfCheck";
import { useSubmissionForm } from "./useSubmissionForm";
import {
  ChatMessageItem,
  hydrateLegacyWarrantyChatHistory,
  mapLegacyChatHistoryToApiMessages,
} from "./warrantyDraftHydration";

interface ChatSubmissionProps {
  householdData: HouseholdData;
  availableAccounts: Account[];
  refreshCases: () => void;
  caseType: CaseType;
}

const ChatSubmission: React.FC<ChatSubmissionProps> = ({
  householdData,
  availableAccounts,
  refreshCases,
  caseType,
}) => {
  const { t } = useTranslation();

  const isWarranty = caseType === CaseType.WARRANTY;
  const translationNamespace = isWarranty ? "warranty" : "propertyManagerCase";

  const navigate = useNavigate();

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const caseRef = useRef<HTMLTextAreaElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const [phoneNumber, setPhoneNumber] = useState(rememberedPhone);
  const [draftId, setDraftId] = useState<number | null>(null);
  // Problems the homeowner listed that still wait for their own case; see issueQueue.ts.
  const [issueQueue, setIssueQueue] = useIssueQueue(householdData.household.id);
  const [justSubmitted, setJustSubmitted] = useState(false);
  // Input waits for the saved draft, so a first message is not overwritten by it. True even when the load fails.
  const [draftLoaded, setDraftLoaded] = useState(caseType !== CaseType.WARRANTY);
  const [requestError, setRequestError] = useState<string | null>(null);
  const gate = useMemo(createRequestGate, []);

  const handlePhoneChange = (value: string) => {
    setPhoneNumber(value);
  };

  const {
    pendingFiles,
    isSubmittingCase,
    setSubmittingState,
    isFileUploaded,
    isFileUploadMandatory,
    submitFiles,
    uploadPendingFiles,
    uploadProgress,
    submitError,
    setSubmitError,
    resetSubmission,
    uploadedDraftFiles,
    setUploadedDraftFiles,
    clientSubmissionId,
  } = useSubmissionForm({ householdData, draftId });

  const [chatHistory, setChatHistory] = useState<ChatMessageItem[]>([]);

  const currentStatus = useMemo(
    () => (chatHistory.length > 0 ? chatHistory[chatHistory.length - 1] : null),
    [chatHistory],
  );

  const waitingIssues = useMemo(
    () => (isWarranty ? mergeIssues(currentStatus?.otherIssues, issueQueue) : []),
    [isWarranty, currentStatus, issueQueue],
  );
  const selfCheck = useSelfCheck(
    householdData.household.id,
    availableAccounts?.[0]?.id,
    isWarranty && isSelfCheckDraft(currentStatus?.status, currentStatus?.isResponse)
      ? currentStatus?.message
      : undefined,
    householdData.documents,
  );
  // Between cases the next waiting issue is offered as the opening message.
  const nextIssue = chatHistory.length === 0 ? waitingIssues[0] : undefined;

  useEffect(() => {
    if (inputRef.current && nextIssue !== undefined) {
      inputRef.current.value = nextIssue;
    }
  }, [nextIssue]);

  useEffect(() => {
    if (caseType !== CaseType.WARRANTY) {
      return;
    }

    let isCancelled = false;
    const token = gate.next();
    getLatestWarrantyDraft(householdData.household.id, WarrantyDraftFlow.LEGACY).then((result) => {
      if (isCancelled) {
        return;
      }
      setDraftLoaded(true);
      if (!gate.isCurrent(token) || !result.success || !result.result) {
        return;
      }
      setDraftId(result.result.draftId);
      setUploadedDraftFiles(result.result.files);
      setChatHistory(hydrateLegacyWarrantyChatHistory(result.result));
    });

    return () => {
      isCancelled = true;
    };
  }, [caseType, gate, householdData.household.id, setUploadedDraftFiles]);

  const submitChatMessage = useCallback(async () => {
    const question = inputRef.current?.value.trim() ?? "";
    const selectedAccount = availableAccounts?.[0]?.id;
    if (!question || !selectedAccount || !draftLoaded) {
      return;
    }

    // Map current history before adding the new question
    const currentApiHistory = mapLegacyChatHistoryToApiMessages(chatHistory);
    const startsWaitingIssue = nextIssue !== undefined;

    inputRef.current!.value = "";
    setRequestError(null);
    const token = gate.next();
    setChatHistory((oldHistory) => [
      ...oldHistory,
      {
        status: NewWarrantyCaseResponseType.MORE_INFO_REQUIRED,
        message: question,
        caseId: null,
        isResponse: false,
        provisionalTitle: null,
      },
    ]);
    const result = await fileNewCase({
      question: question,
      householdId: householdData.household.id,
      history: currentApiHistory,
      accountId: selectedAccount,
      type: caseType,
      draftId,
      flowType: WarrantyDraftFlow.LEGACY,
    });

    if (!gate.isCurrent(token)) {
      return;
    }
    if (result.success) {
      if (startsWaitingIssue) {
        setIssueQueue(issueQueue.slice(1));
      }
      setJustSubmitted(false);
      setDraftId(result.result.draftId ?? draftId);
      setChatHistory((oldHistory) => [
        ...oldHistory,
        {
          ...result.result,
          isResponse: true,
        },
      ]);
    } else {
      setRequestError(result.message);
      setChatHistory((oldHistory) => oldHistory.slice(0, -1));
      if (inputRef.current) {
        inputRef.current.value = question; // Restore input field
      }
    }
  }, [
    householdData,
    availableAccounts,
    caseType,
    chatHistory,
    draftId,
    nextIssue,
    issueQueue,
    setIssueQueue,
    draftLoaded,
    gate,
  ]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const value = caseRef?.current?.value.trim() ?? "";
      const selectedAccount = availableAccounts?.[0]?.id;
      if (!value || !selectedAccount) {
        return;
      }

      setSubmittingState(true);
      const uploads = await uploadPendingFiles();
      if (!uploads) {
        setSubmittingState(false);
        return;
      }
      // Map history for submission
      const currentApiHistory = mapLegacyChatHistoryToApiMessages(chatHistory);
      const result = await forceSubmitCase(
        {
          description: value + ("\n\n" + t("warranty.contactPersonsPhoneNumber") + ": " + phoneNumber.trim()),
          householdId: householdData.household.id,
          title: titleRef.current?.value ?? "Unknown",
          accountId: selectedAccount,
          phoneNumber: phoneNumber.trim(),
          type: caseType,
          draftId,
          clientSubmissionId,
        },
        uploads,
        currentApiHistory,
      );
      if (result.success) {
        rememberPhone(phoneNumber.trim());
        if (waitingIssues.length === 0) {
          navigate(`/warranty/${result.result.id}`);
          return;
        }
        // The homeowner listed more problems: stay here and offer the next one.
        setIssueQueue(waitingIssues);
        setDraftId(null);
        setChatHistory([]);
        resetSubmission();
        refreshCases();
        setJustSubmitted(true);
      } else {
        sendToSentry(new Error(result.message), {
          additionalContext: {
            component: "ChatSubmission.submitForm",
            result,
          },
        });
        setSubmitError(result.message);
        setSubmittingState(false);
      }
    },
    [
      availableAccounts,
      setSubmittingState,
      t,
      phoneNumber,
      householdData.household.id,
      uploadPendingFiles,
      setSubmitError,
      navigate,
      caseType,
      chatHistory,
      draftId,
      waitingIssues,
      setIssueQueue,
      resetSubmission,
      refreshCases,
      clientSubmissionId,
    ],
  );

  const skipNextIssue = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setJustSubmitted(false);
    setIssueQueue(waitingIssues.slice(1));
  }, [waitingIssues, setIssueQueue]);

  // Clear first and invalidate pending replies, so a late reply cannot bring back the deleted draft.
  const resetChat = useCallback(async () => {
    gate.next();
    const discardedDraftId = draftId;
    setRequestError(null);
    setDraftId(null);
    setChatHistory([]);
    resetSubmission();
    if (discardedDraftId) {
      const result = await deleteWarrantyDraft(discardedDraftId);
      if (!result.success) {
        setRequestError(result.message);
      }
    }
    refreshCases();
  }, [gate, draftId, refreshCases, resetSubmission]);

  return (
    <>
      <span className="flex items-center">
        <h3 className="text-xl font-semibold inline">{t(`${translationNamespace}.submitNewCase`)}</h3>
        {currentStatus && (
          <Button variant="outline" size="sm" className="ml-2" onClick={resetChat}>
            {t(`${translationNamespace}.cancel`)}
          </Button>
        )}
      </span>
      <div className="prose prose-sm prose-gray text-gray-500">
        <div>
          <ul className="m-0">
            {isWarranty ? (
              <>
                <li className="m-0">{t("warrantyIntake.tipOneMessage")}</li>
                <li className="m-0">{t("warrantyIntake.tipSeveral")}</li>
              </>
            ) : (
              <li className="m-0">{t(`${translationNamespace}.submitOneCase`)}</li>
            )}
            {isFileUploadMandatory && <li className="m-0">{t(`${translationNamespace}.mandatoryFile`)}</li>}
            {isFileUploadMandatory && <li className="m-0">{t(`${translationNamespace}.pictureDescription`)}</li>}
            {!isWarranty && (
              <li>
                <span className="inline-block">{t(`${translationNamespace}.problemDescriptionInstructions`)}</span>
                <ul className="m-0">
                  <li className="m-0">{t(`${translationNamespace}.defectDate`)}</li>
                  <li className="m-0">{t(`${translationNamespace}.defectLocation`)}</li>
                  <li className="m-0">{t(`${translationNamespace}.defectDescription`)}</li>
                </ul>
              </li>
            )}
          </ul>
        </div>
      </div>
      {chatHistory.length > 0 && (
        <ChatMessageHolder>
          {chatHistory
            .filter((m) => m.status === NewWarrantyCaseResponseType.MORE_INFO_REQUIRED)
            .map((m, i) => (
              <ChatMessage
                key={i}
                message={m.message}
                type={m.isResponse ? ChatMessageType.RESPONSE : ChatMessageType.QUESTION}
              />
            ))}
          {currentStatus?.isResponse === false && (
            <ChatMessage
              className="animate-pulse font-bold px-4 py-1 text-lg"
              message={"..."}
              type={ChatMessageType.RESPONSE}
            />
          )}
        </ChatMessageHolder>
      )}
      {justSubmitted && nextIssue !== undefined && <Banner success message={t("warrantyIntake.submitted")} />}
      {nextIssue !== undefined && (
        <p className="m-0 text-sm text-gray-700">{t("warrantyIntake.nextIssue", { count: waitingIssues.length })}</p>
      )}
      {(!currentStatus || currentStatus?.status === NewWarrantyCaseResponseType.MORE_INFO_REQUIRED) && (
        <>
          <textarea
            className="border border-gray-300 rounded-md p-2 w-full whitespace-pre-wrap"
            name="report"
            rows={5}
            placeholder={t(`${translationNamespace}.describeCase`)}
            ref={inputRef}
            disabled={!draftLoaded}
            onKeyDown={(e) => {
              if (isSubmitShortcut(e) && (!currentStatus || currentStatus.isResponse)) {
                e.preventDefault();
                submitChatMessage();
              }
            }}
          />
          {requestError && <p className="m-0 text-sm text-red-600">{requestError}</p>}
          {(!currentStatus || currentStatus?.isResponse) && (
            <Button variant="primary" onClick={() => submitChatMessage()} disabled={!draftLoaded}>
              {currentStatus?.status === NewWarrantyCaseResponseType.MORE_INFO_REQUIRED
                ? t(`${translationNamespace}.answer`)
                : t(`${translationNamespace}.startSubmission`)}
            </Button>
          )}
          {nextIssue !== undefined && (
            <Button variant="outline" onClick={skipNextIssue}>
              {t("warrantyIntake.skipIssue")}
            </Button>
          )}
        </>
      )}
      {currentStatus && currentStatus?.status !== NewWarrantyCaseResponseType.MORE_INFO_REQUIRED && (
        <>
          <hr className="border-2" />
          {intakeVerdict(currentStatus.status) === "notCovered" && (
            <Banner success={false} message={t(`${translationNamespace}.notCovered`)} />
          )}
          {intakeVerdict(currentStatus.status) === "covered" && (
            <Banner success message={t(`${translationNamespace}.descriptionIsSufficient`)} />
          )}
          {isWarranty && currentStatus.builderNote && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
              <p className="m-0 font-semibold">{t("warrantyIntake.builderNoteTitle")}</p>
              <p className="m-0">{currentStatus.builderNote}</p>
            </div>
          )}
          {selfCheck && <SelfCheckTip selfCheck={selfCheck} />}
          {waitingIssues.length > 0 && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-900">
              <p className="m-0 font-semibold">{t("warrantyIntake.otherIssuesTitle")}</p>
              <ul className="m-0 list-disc pl-5">
                {waitingIssues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
              <p className="m-0">{t("warrantyIntake.otherIssuesHint")}</p>
            </div>
          )}
          <SubmissionForm
            handleSubmit={handleSubmit}
            pendingFiles={pendingFiles}
            uploadedDraftFiles={uploadedDraftFiles}
            onPhoneChange={handlePhoneChange}
            phoneNumber={phoneNumber}
            isFileUploadMandatory={isFileUploadMandatory}
            isFileUploaded={isFileUploaded}
            isSubmittingCase={isSubmittingCase}
            submitFiles={submitFiles}
            uploadProgress={uploadProgress}
            submitError={submitError}
          >
            <input
              className="hidden"
              type="hidden"
              value={currentStatus.provisionalTitle ?? "Unknown"}
              ref={titleRef}
            />
            <textarea
              className="border border-gray-300 rounded-md p-2 w-full"
              name="report"
              rows={5}
              placeholder={t(`${translationNamespace}.caseDescription`)}
              defaultValue={currentStatus.message}
              ref={caseRef}
              required
              onKeyDown={(e) => {
                if (isSubmitShortcut(e)) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
            />
          </SubmissionForm>
        </>
      )}
    </>
  );
};

export default ChatSubmission;
